import React, {useEffect, useState, useRef} from 'react';
import axios from 'axios';
import {useStoreState, useStoreActions} from 'easy-peasy';
import Button from '@/components/ui/Button';
import {flexRender, getCoreRowModel, useReactTable, Row, Table} from '@tanstack/react-table';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';
import {ModalAlert, useAlert} from '@/components/alerts';
import {buildNotificationURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface User {
    id: number;
    username: string;
    email: string;
}

interface Notification {
    id: number;
    usersConfigured: User[];
}

interface NotificationUserConfigureTableProps {
    notificationId: number;
}

interface IndeterminateCheckboxProps {
    indeterminate?: boolean;
    className?: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const NotificationUserConfigureTable: React.FC<NotificationUserConfigureTableProps> = React.memo(({notificationId}) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {showSuccess} = useAlert();

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState<boolean>(false);
    const [usersToRemove, setUsersToRemove] = useState<number[]>([]);
    const [isRemoving, setIsRemoving] = useState<boolean>(false);

    const notifications = useStoreState((state: any) => state.notifications.items) as Notification[];
    const deleteNotificationConfigure = useStoreActions((actions: any) => actions.notifications.deleteNotificationConfigure);

    const activeNotificationIndex = notifications.findIndex((n) => n.id === notificationId);
    if (activeNotificationIndex === -1) {
        return null;
    }

    const activeNotification = notifications[activeNotificationIndex];

    const userCanDeleteNotificationPerm = hasPermission('fs.notification.user_assignment.delete', userData?.permissions, true);

    useEffect(() => {
        if (rowSelection) {
            setSelectedUsers(Object.keys(rowSelection).map(Number).filter((key) => rowSelection[key]));
        }
    }, [rowSelection]);

    const deleteBulk = async (): Promise<void> => {
        setUsersToRemove(selectedUsers);
        setRemoveConfirmOpen(true);
    };

    const closeRemoveConfirm = (): void => {
        setUsersToRemove([]);
        setRemoveConfirmOpen(false);
        setIsRemoving(false);
    };

    const confirmRemove = async (): Promise<void> => {
        if (usersToRemove.length === 0) {return;}

        setIsRemoving(true);
        try {
            const url = buildNotificationURL('removeNotificationPermBulk', notificationId);
            await axios.post(url, {userIds: usersToRemove});
            usersToRemove.forEach((userId) => deleteNotificationConfigure([notificationId, userId]));
            setSelectedUsers([]);
            setRowSelection({});
            showSuccess(i18n.t('jt_users_removed_successfully.text'));
            closeRemoveConfirm();
        } catch (error) {
            setIsRemoving(false);
            throw error;
        }
    };

    const revokeAccessButton = (
        userCanDeleteNotificationPerm ? (
            <div className="mt-3">
                <Button
                    variant="danger"
                    className="float-right"
                    size="sm"
                    onClick={deleteBulk}
                    disabled={selectedUsers.length === 0}
                    data-cy="notification-user-configure-table-danger-btn">
                    {i18n.t('jt_remove_recipients.text')} ({selectedUsers.length})
                </Button>
            </div>
        ) : null
    );

    const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
        indeterminate,
        className = '',
        ...rest
    }) => {
        const ref = useRef<HTMLInputElement>(null);

        useEffect(() => {
            if (typeof indeterminate === 'boolean' && ref.current) {
                ref.current.indeterminate = !rest.checked && indeterminate;
            }
        }, [ref, indeterminate, rest.checked]);

        return (
            <input
                type="checkbox"
                ref={ref}
                className={className + ' cursor-pointer'}
                {...rest}
            />
        );
    };

    const columns = [
        {
            id: 'select',
            header: ({table}: { table: Table<User> }) => (
                <IndeterminateCheckbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({row}: { row: Row<User> }) => (
                <div className="px-1">
                    <IndeterminateCheckbox
                        checked={row.getIsSelected()}
                        disabled={!row.getCanSelect()}
                        indeterminate={row.getIsSomeSelected()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'username',
            header: i18n.t('th_username_email.text'),
            cell: (cell: { row: { original: User } }) => `${cell.row.original.username} (${cell.row.original.email})`,
        },
    ];

    const data = activeNotification.usersConfigured;

    const table = useReactTable({
        data,
        columns,
        getRowId: (row) => row.id.toString(),
        state: {
            rowSelection,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
    });

    const noDataIndication = i18n.t('jt_no_data_available.text');

    if (!loadingUserData) {
        return (
            <>
                <table className="table table-hover table-bordered g-table w-100">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    {
                        table.getRowModel().rows.length === 0 && (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length}>
                                        {noDataIndication}
                                    </td>
                                </tr>
                            </tbody>
                        )
                    }
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {revokeAccessButton}
                <ModalAlert
                    isOpen={removeConfirmOpen}
                    onClose={closeRemoveConfirm}
                    variant="warning"
                    title={i18n.t('jt_confirm_action.text')}
                    message={`${i18n.t('jt_are_you_sure_remove_recipients.text')} ${usersToRemove.length} ${usersToRemove.length === 1 ? i18n.t('jt_user_singular.text') : i18n.t('jt_user_plural.text')}?`}
                    actions={[
                        {
                            label: i18n.t('jt_remove_recipients.text'),
                            onClick: confirmRemove,
                            variant: 'danger',
                            loading: isRemoving
                        }
                    ]}
                    dismissible={!isRemoving}
                />
            </>
        );
    } else {
        return (
            <CellLoader fullHeight={true} />
        );
    }
});

export default NotificationUserConfigureTable; 